import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminApi, type AdminTournament } from '@/api/admin';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CasinoButton } from '@/components/CasinoButton';
import { PayoutEditor } from '@/components/admin/PayoutEditor';
import { useAppConfig } from '@/hooks/useAppConfig';
import {
  computePotCents,
  formatDollars,
  validatePayoutStructure,
  type PayoutSlot,
} from '@/lib/payouts';

const inputCls =
  'w-full rounded-md bg-black/50 border-2 border-cabinet-cream/20 px-3 py-2.5 text-cabinet-cream placeholder:text-cabinet-cream/40 focus:border-cabinet-gold focus:outline-none focus:ring-2 focus:ring-cabinet-gold/40 transition tabular-nums';

export function TournamentNew() {
  const navigate = useNavigate();

  const activeQ = useQuery({
    queryKey: ['admin', 'tournaments', 'active'],
    queryFn: adminApi.activeTournament,
  });

  const config = useAppConfig();
  const [name, setName] = useState('');
  // Seed from MACHINE_NAME env (via /api/config) so deployments can rename
  // the default cabinet without a code change. Admin can override per-tourney.
  const [machineName, setMachineName] = useState(config.machineName);
  const [entryFee, setEntryFee] = useState('25'); // dollars, kept as string so the field can be cleared
  const [maxEntries, setMaxEntries] = useState('2');
  const [pullCount, setPullCount] = useState('10');
  const [creditsPerPull, setCreditsPerPull] = useState('3');
  const [tiebreakerPulls, setTiebreakerPulls] = useState('10');
  const [expectedEntries, setExpectedEntries] = useState('8');
  const [slots, setSlots] = useState<PayoutSlot[]>([
    { place: 1, pct: 50 },
    { place: 2, pct: 35 },
    { place: 3, pct: 15 },
  ]);
  const [error, setError] = useState<string | null>(null);

  const entryFeeCents = Math.max(0, Math.round((parseFloat(entryFee) || 0) * 100));
  const expectedEntriesNum = Math.max(1, parseInt(expectedEntries, 10) || 1);
  const potCents = useMemo(
    () => computePotCents(entryFeeCents, expectedEntriesNum),
    [entryFeeCents, expectedEntriesNum],
  );

  const payoutValid = validatePayoutStructure(slots);

  const create = useMutation({
    mutationFn: adminApi.createTournament,
    onSuccess: ({ tournament }) => navigate(`/admin/tournament/${tournament.id}/manage`),
    onError: (err) => {
      const e = err as { error?: string; message?: string };
      setError(e.message ?? e.error ?? 'Could not create tournament.');
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!payoutValid.ok) {
      setError(payoutValid.error ?? 'invalid payout');
      return;
    }
    create.mutate({
      name: name.trim(),
      machineName: machineName.trim() || config.machineName,
      entryFeeCents,
      maxEntriesPerPlayer: Math.max(1, parseInt(maxEntries, 10) || 1),
      pullCount: Math.max(1, parseInt(pullCount, 10) || 1),
      creditsPerPull: Math.max(1, parseInt(creditsPerPull, 10) || 1),
      tiebreakerPulls: Math.max(1, parseInt(tiebreakerPulls, 10) || 1),
      payoutStructure: slots,
    });
  }

  const blockedByActive = activeQ.data?.tournament ?? null;

  return (
    <main className="mx-auto max-w-2xl px-5 py-8 space-y-6">
      <AdminHeader
        title="New tournament"
        crumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Tournaments', to: '/admin' },
          { label: 'New' },
        ]}
      />

      {blockedByActive && (
        <ActiveTournamentBanner t={blockedByActive} />
      )}

      <form onSubmit={submit} className="space-y-6">
        <section className="cabinet-card p-5 space-y-4">
          <h2 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
            Tournament details
          </h2>
          <Field label="Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Spring 2026 Open"
              className={inputCls}
            />
          </Field>
          <Field label="Machine name">
            <input
              value={machineName}
              onChange={(e) => setMachineName(e.target.value)}
              className={inputCls}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Entry fee">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cabinet-cream/40">$</span>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  value={entryFee}
                  onChange={(e) => setEntryFee(e.target.value)}
                  className={`${inputCls} pl-6`}
                />
              </div>
            </Field>
            <Field label="Max entries/player">
              <input
                type="number"
                min={1}
                max={10}
                value={maxEntries}
                onChange={(e) => setMaxEntries(e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Pull count">
              <input
                type="number"
                min={1}
                max={100}
                value={pullCount}
                onChange={(e) => setPullCount(e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Credits per pull">
              <input
                type="number"
                min={1}
                max={100}
                value={creditsPerPull}
                onChange={(e) => setCreditsPerPull(e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Tiebreaker pulls">
              <input
                type="number"
                min={1}
                max={50}
                value={tiebreakerPulls}
                onChange={(e) => setTiebreakerPulls(e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        <section className="cabinet-card p-5 space-y-4">
          <header className="flex items-center justify-between">
            <h2 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
              Prize split
            </h2>
            <ExpectedEntriesStepper
              value={expectedEntries}
              onChange={setExpectedEntries}
            />
          </header>
          <p className="text-xs text-cabinet-cream/50">
            Used only to preview prize split — actual pot is calculated from real entries.
          </p>
          <div className="rounded-md bg-black/40 ring-1 ring-cabinet-gold/20 px-4 py-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-cabinet-cream/60">
              Preview pot
            </span>
            <span className="font-display text-cabinet-gold-bright text-2xl tabular-nums">
              {formatDollars(potCents)}
            </span>
          </div>

          <PayoutEditor potCents={potCents} slots={slots} onChange={setSlots} />
        </section>

        {error && (
          <div className="rounded-md bg-cabinet-flame-red/20 border border-cabinet-flame-red px-3 py-2 text-sm text-cabinet-cream">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <CasinoButton
            type="submit"
            disabled={
              create.isPending ||
              !name.trim() ||
              !payoutValid.ok ||
              !!blockedByActive
            }
          >
            {create.isPending ? 'Creating…' : 'Create tournament'}
          </CasinoButton>
          <Link
            to="/admin"
            className="text-sm text-cabinet-cream/60 hover:text-cabinet-gold"
          >
            Cancel
          </Link>
        </div>
        <p className="text-xs text-cabinet-cream/50">
          Tournament is created in <strong>Setup</strong> state. You can review and tweak
          settings, then start it from the manage page.
        </p>
      </form>
    </main>
  );
}

function ActiveTournamentBanner({ t }: { t: AdminTournament }) {
  return (
    <div className="rounded-xl border-2 border-cabinet-gold bg-cabinet-purple-deep px-4 py-3 text-sm">
      <p className="font-display uppercase tracking-widest text-cabinet-gold text-xs mb-1">
        There's already an active tournament
      </p>
      <p className="text-cabinet-cream/80">
        “{t.name}” is currently active. Only one tournament can be active at a time —
        close it before you start a new one.
      </p>
      <Link
        to={`/admin/tournament/${t.id}/manage`}
        className="mt-2 inline-block text-cabinet-gold underline-offset-2 hover:underline"
      >
        Manage active tournament →
      </Link>
    </div>
  );
}

function ExpectedEntriesStepper({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const n = Math.max(1, parseInt(value, 10) || 1);
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] uppercase tracking-widest text-cabinet-cream/50">
        Expected entries
      </span>
      <button
        type="button"
        onClick={() => onChange(String(Math.max(1, n - 1)))}
        className="h-7 w-7 rounded-md bg-cabinet-cream/5 ring-1 ring-cabinet-cream/20 text-cabinet-cream hover:bg-cabinet-cream/10"
      >
        −
      </button>
      <input
        type="number"
        min={1}
        max={999}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-14 rounded-md bg-black/50 border border-cabinet-cream/20 px-2 py-1 text-center font-led text-cabinet-led-on tabular-nums focus:border-cabinet-gold focus:outline-none"
      />
      <button
        type="button"
        onClick={() => onChange(String(n + 1))}
        className="h-7 w-7 rounded-md bg-cabinet-gold text-cabinet-purple-ink font-bold hover:brightness-105"
      >
        +
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

export function AdminHeader({
  title,
  crumbs,
}: {
  title: string;
  crumbs?: import('@/components/Breadcrumbs').Crumb[];
}) {
  return (
    <header className="space-y-1.5">
      {crumbs && <Breadcrumbs crumbs={crumbs} />}
      <h1 className="font-display text-cabinet-gold text-2xl uppercase tracking-widest">
        {title}
      </h1>
    </header>
  );
}

